<template>
  <v-card :class="`sticky-box-wrapper pt-4 mx-4 mb-4 ${$vuetify.breakpoint.lgAndUp ? 'mt-6 ' : 'my-4 '}`">
    <div class="px-4">
      <p class="text-caption uppercase mb-2">{{ $t(' ') }}</p>
      <div class="d-flex justify-space-between">
        <p class="grey-font text-body-1 mb-0">
          {{ $t('STICKYBOX_SOCIAL_REASON') }}
        </p>
      </div>
      <!-- client data -->
      <div class="mb-2 client-info">
        <p class="text-h5 mb-0 left-value" :title="socialReason">
          {{ socialReason }}
        </p>
        <template v-if="Boolean(cif)">
          <p class="grey-font text-body-1 mt-2 mb-0">{{ $t('STICKYBOX_CIF') }}</p>
          <p class="text-subtitle-1 mb-0 right-value" :title="cif">
            {{ cif }}
          </p>
        </template>
      </div>
      <div class="d-flex justify-space-between">
        <p class="grey-font text-body-1 mb-0">
          {{ $t('STICKYBOX_COMPROMISED') }}
        </p>
      </div>
      <!-- client data -->
      <div class="d-flex justify-space-between">
        <p class="text-h5  mb-1">{{ commitedDeliveries }} / {{ $t('STICKYBOX_MONTH') }}</p>
        <div class="right-column pl-5">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" x-small outlined @click="copyOfferId">
                {{ $t('STICKYBOX_COPY_OFFERID') }}
              </v-btn>
            </template>

            <div>{{ offerId }}</div>
          </v-tooltip>
        </div>
      </div>
      <!-- international shipments -->
      <div class="d-flex justify-space-between">
        <p class="grey-font text-body-1 mb-0">
          {{ $t('STICKYBOX_INTERNATIONAL') }}
        </p>
      </div>
      <div class="d-flex justify-space-between">
        <p class="text-h5 mb-1">{{ internationalShipments }} / {{ $t('STICKYBOX_MONTH') }}</p>
      </div>
    </div>
    <!-- grey section -->
    <div v-if="step !== 1" class="dark-card pa-4">
      <p class="text-caption uppercase mb-1">
        {{ $t('STICKYBOX_VALIDATION_TITLE') }}
      </p>

      <!-- Type and version -->
      <div class="d-flex justify-space-between">
        <p class="grey-font text-body-1 mb-0">
          {{ $t('STICKYBOX_OFFER_TYPE') }}
        </p>
        <p class="grey-font text-body-1 mb-0 right-column">
          {{ $t('STICKYBOX_OFFER_VERSION') }}
        </p>
      </div>
      <div class="d-flex justify-space-between">
        <p class="text-h5 mb-0 " data-cy="offer-type">
          {{ offerTypeText }}
        </p>
        <p class="text-h5 mb-0 right-column" data-cy="offer-version">{{ offerVersion }}</p>
      </div>
      <!-- validations -->
      <div class="d-flex justify-space-between">
        <p class="grey-font text-body-1 mb-0">
          {{ $t('STICKYBOX_VALID_STATUS') }}
        </p>
        <p class="grey-font text-body-1 mb-0 right-column">
          {{ $t('STICKYBOX_APPROB_STATUS') }}
        </p>
      </div>
      <div class="d-flex justify-space-between mb-2">
        <p :class="['text-h5 mb-0 ', isOfferValid ? 'success-text' : 'primary--text']" data-cy="offer-validation">
          {{ isValidOfferText }}
        </p>
        <p :class="['text-h5 mb-2 right-column', offerStatusText.class]" data-cy="offer-status">
          {{ offerStatusText.label }}
        </p>
      </div>
      <div v-if="hasSignatureProcessStarted" class="d-flex justify-space-between mb-4">
        <div>
          <p class="grey-font text-body-1 mb-0 right-column">
            {{ $t('SIGNATURE_STATUS') }}
          </p>
          <p class="text-h5 mb-0">
            {{ signStatusText }}
          </p>
        </div>
      </div>
      <slot />
    </div>
  </v-card>
</template>

<script>
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import { mapState, mapGetters } from 'vuex'
import { OFFER_STATUS, VALIDATION_STATUS } from '@/helpers/constants'

export default {
  name: 'SummaryCardContent',
  data() {
    return {
      loadingCancelation: false,
      loadingSignature: false,
    }
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM, 'isManuallySigned']),
    ...mapGetters('roles', ['getRole']),
    ...mapState('process', ['signStatus']),
    ...mapGetters('process', ['isOfferValid']),
    isUserAnEvaluator() {
      return this.getRole === 'evaluator'
    },
    validationStatus() {
      return this[GET_OFFER_ITEM]('validationStatus')
    },
    cif() {
      return this[GET_OFFER_ITEM]('clientCif')
    },
    commitedDeliveries() {
      return this[GET_OFFER_ITEM]('clientCommitedDeliveries')
    },
    internationalShipments() {
      return this[GET_OFFER_ITEM]('internationalShipments')
    },
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    socialReason() {
      return this[GET_OFFER_ITEM]('clientSocialReason')
    },
    offerStatus() {
      return this[GET_OFFER_ITEM]('status')
    },
    offerStatusText() {
      const text = {
        label: this.validationStatus,
        class: 'primary--text',
      }
      if (this.validationStatus === VALIDATION_STATUS.APPROVED) {
        text.label = this.$t('STICKYBOX_APPROVED')
        text.class = 'success-text'
      } else if (this.validationStatus === VALIDATION_STATUS.PENDING) {
        text.label =
          this.offerStatus === OFFER_STATUS.VALIDATING
            ? this.$t('STICKYBOX_VALIDATING')
            : this.$t('STICKYBOX_UNAPPROVED')
      } else if (this.validationStatus === VALIDATION_STATUS.REJECTED) {
        text.label = this.$t('STICKYBOX_REJECTED')
      } else if (this.validationStatus === VALIDATION_STATUS.RETURNED) {
        text.label = this.$t('STICKYBOX_RETURNED')
      }
      return text
    },
    offerVersion() {
      return this[GET_OFFER_ITEM]('finalVersion')
    },
    signStatusText() {
      return this.isManuallySigned
        ? this.$t('MANUALLY_SIGNED')
        : this.$t(this.signStatus.toUpperCase())
    },
    step() {
      return this[GET_OFFER_ITEM]('step')
    },
    offerTypeText() {
      return this.$t(`STICKYBOX_TYPE${this.offerType}`)
    },
    isValidOfferText() {
      return this.isOfferValid ? this.$t('STICKYBOX_VALID') : this.$t('STICKYBOX_INVALID')
    },
    hasSignatureProcessStarted() {
      return this.offerStatus === OFFER_STATUS.SIGNING || this.offerStatus === OFFER_STATUS.SIGNED
    },
    offerId() {
      return this[GET_OFFER_ITEM]('offerId')
    },
  },
  methods: {
    copyOfferId() {
      navigator.clipboard.writeText(this.offerId)
    },
  },
}
</script>

<style scoped lang="scss">
.sticky-box-wrapper {
  min-width: 300px;
  max-width: 340px;

  @media (min-width: 960px) and (max-width: 1263px) {
    button.v-btn {
      letter-spacing: 0 !important;
    }
  }
}

.dark-card {
  background-color: $light-grey;
}

.right-column {
  min-width: rem(150);
}

.client-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.left-value {
  max-width: rem(320);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.right-value {
  max-width: rem(200);
  word-break: break-all;
}

.success-text {
  color: $ok-green;
}
</style>
